import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ChevronDown, ChevronRight } from "lucide-react";
import { fetchCalendarEvents, createCalendarEvent, updateCalendarEvent, deleteCalendarEvent, fetchCalendarCases, createCalendarCase, updateCalendarCase, deleteCalendarCase, fetchMatters, fetchCalendarCategories, createCalendarCategory, updateCalendarCategory, deleteCalendarCategory, initDefaultCategories } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { todayISO } from "../lib/dates";

function ymFirst(d){ const x = new Date(d.getFullYear(), d.getMonth(), 1); return x; }
function addMonths(d, n){ const x = new Date(d); x.setMonth(x.getMonth()+n); return x; }
function daysInMonth(d){ return new Date(d.getFullYear(), d.getMonth()+1, 0).getDate(); }

// Default category colors
const DEFAULT_CATEGORY_COLORS = {
  "Filing Deadline": "#EE2D24",
  "Hearing": "#3B82F6",
  "Deposition": "#F97316",
  "Client Meeting": "#10B981",
  "Discovery Deadline": "#A855F7",
  "Mediation": "#EAB308",
  "Trial": "#0F172A"
};

// Legacy type colors for backward compatibility
const TYPE_COLORS = {
  Filing: "#EE2D24",
  Hearing: "#0f172a",
  Deposition: "#f97316",
  Other: "#475569"
};

export default function CalendarPage(){
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab") || "month";
  const [tab, setTab] = useState(tabFromUrl);
  const [monthCursor, setMonthCursor] = useState(ymFirst(new Date()));
  const [events, setEvents] = useState([]);
  const [cases, setCases] = useState([]);
  const [matters, setMatters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesInitialized, setCategoriesInitialized] = useState(false);
  
  // Expandable cases state
  const [expandedCases, setExpandedCases] = useState({});
  
  // List view filters
  const [selectedCaseFilter, setSelectedCaseFilter] = useState("all");
  
  // Day detail modal
  const [dayDetailOpen, setDayDetailOpen] = useState(false);
  const [selectedDayDate, setSelectedDayDate] = useState(null);
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);

  // Helper to get color for event type
  const getEventColor = (type) => {
    const category = categories.find(c => c.name === type);
    if (category) return category.color;
    // Fallback to legacy colors
    return TYPE_COLORS[type] || DEFAULT_CATEGORY_COLORS[type] || '#111827';
  };
  
  function openDayDetail(date, events) {
    setSelectedDayDate(date);
    setSelectedDayEvents(events);
    setDayDetailOpen(true);
  }

  // Sync tab with URL
  useEffect(() => {
    const urlTab = searchParams.get("tab") || "month";
    if (urlTab !== tab) {
      setTab(urlTab);
    }
  }, [searchParams]);

  const handleTabChange = (newTab) => {
    setTab(newTab);
    setSearchParams({ tab: newTab });
  };

  // List View controls
  const [fromDate, setFromDate] = useState(todayISO());
  const [rangeDays, setRangeDays] = useState(30); // customizable up to 365

  // Event modal state
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    caseId: "none",
    type: "Filing Deadline",
    title: "",
    startDate: todayISO(),
    startTime: "",
    endTime: "",
    notes: ""
  });

  // Case modal state
  const [caseModalOpen, setCaseModalOpen] = useState(false);
  const [editingCase, setEditingCase] = useState(null);
  const [caseForm, setCaseForm] = useState({
    name: "",
    caseNumber: "",
    matterName: "",
    matterId: "none",
    courtSystem: "",
    courtName: "",
    description: ""
  });

  // Category modal state
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    color: "#EE2D24"
  });

  async function loadData() {
    try{
      // For month view, load events for current month
      // For list view, we'll load events separately based on the date range
      const monthFrom = monthCursor.toISOString().slice(0,10);
      const monthTo = addMonths(monthCursor, 1).toISOString().slice(0,10);
      
      const [ev, cs, ms, cats] = await Promise.all([
        fetchCalendarEvents({ from: monthFrom, to: monthTo }),
        fetchCalendarCases(),
        fetchMatters(),
        fetchCalendarCategories()
      ]);
      setEvents(ev); setCases(cs); setMatters(ms); setCategories(cats);
      
      // Initialize default categories if none exist and not already initialized
      if (cats.length === 0 && !categoriesInitialized) {
        setCategoriesInitialized(true);
        await initDefaultCategories();
        const newCats = await fetchCalendarCategories();
        setCategories(newCats);
      }
    }catch(e){ 
      console.error(e);
      toast({ variant: "destructive", title: "Error loading data" });
    }
  }

  // Separate function to load events for list view based on custom date range
  async function loadListViewEvents() {
    try {
      const start = new Date(fromDate);
      const end = new Date(start);
      end.setDate(end.getDate() + Math.min(365, Math.max(1, parseInt(rangeDays, 10))));
      
      const listEvents = await fetchCalendarEvents({
        from: start.toISOString().slice(0, 10),
        to: end.toISOString().slice(0, 10)
      });
      
      setEvents(listEvents);
    } catch (e) {
      console.error(e);
      toast({ variant: "destructive", title: "Error loading list view events" });
    }
  }

  useEffect(() => {
    loadData();
  }, [monthCursor]);

  // Load events for list view when tab changes to list or date range changes
  useEffect(() => {
    if (tab === 'list') {
      loadListViewEvents();
    }
  }, [tab, fromDate, rangeDays]);

  // Event CRUD handlers
  function openEventModal(event = null) {
    if (event) {
      setEditingEvent(event);
      setEventForm({
        caseId: event.caseId || "none",
        type: event.type || (categories.length > 0 ? categories[0].name : "Filing Deadline"),
        title: event.title || "",
        startDate: event.startDate || todayISO(),
        startTime: event.startTime || "",
        endTime: event.endTime || "",
        notes: event.notes || ""
      });
    } else {
      setEditingEvent(null);
      setEventForm({
        caseId: "none",
        type: categories.length > 0 ? categories[0].name : "Filing Deadline",
        title: "",
        startDate: todayISO(),
        startTime: "",
        endTime: "",
        notes: ""
      });
    }
    setEventModalOpen(true);
  }

  async function saveEvent() {
    try {
      const payload = {
        ...eventForm,
        caseId: eventForm.caseId === "none" ? null : eventForm.caseId
      };
      if (editingEvent) {
        await updateCalendarEvent(editingEvent.id, payload);
        toast({ title: "Event updated" });
      } else {
        await createCalendarEvent(payload);
        toast({ title: "Event created" });
      }
      setEventModalOpen(false);
      // Reload appropriate data based on current tab
      if (tab === 'list') {
        await loadListViewEvents();
      } else {
        await loadData();
      }
    } catch (e) {
      console.error(e);
      toast({ variant: "destructive", title: "Error saving event" });
    }
  }

  async function handleDeleteEvent(eventId) {
    if (!window.confirm("Delete this event?")) return;
    try {
      await deleteCalendarEvent(eventId);
      toast({ title: "Event deleted" });
      // Reload appropriate data based on current tab
      if (tab === 'list') {
        await loadListViewEvents();
      } else {
        await loadData();
      }
    } catch (e) {
      console.error(e);
      toast({ variant: "destructive", title: "Error deleting event" });
    }
  }

  // Case CRUD handlers
  function openCaseModal(caseItem = null) {
    if (caseItem) {
      setEditingCase(caseItem);
      setCaseForm({
        name: caseItem.name || "",
        caseNumber: caseItem.caseNumber || "",
        matterName: caseItem.matterName || "",
        matterId: caseItem.matterId || "none",
        courtSystem: caseItem.courtSystem || "",
        courtName: caseItem.courtName || "",
        description: caseItem.description || ""
      });
    } else {
      setEditingCase(null);
      setCaseForm({
        name: "",
        caseNumber: "",
        matterName: "",
        matterId: "none",
        courtSystem: "",
        courtName: "",
        description: ""
      });
    }
    setCaseModalOpen(true);
  }

  async function saveCase() {
    try {
      const payload = {
        ...caseForm,
        matterId: caseForm.matterId === "none" ? null : caseForm.matterId
      };
      if (editingCase) {
        await updateCalendarCase(editingCase.id, payload);
        toast({ title: "Case updated" });
      } else {
        await createCalendarCase(payload);
        toast({ title: "Case created" });
      }
      setCaseModalOpen(false);
      loadData();
    } catch (e) {
      console.error(e);
      toast({ variant: "destructive", title: "Error saving case" });
    }
  }

  async function handleDeleteCase(caseId) {
    if (!window.confirm("Delete this case?")) return;
    try {
      await deleteCalendarCase(caseId);
      toast({ title: "Case deleted" });
      loadData();
    } catch (e) {
      console.error(e);
      toast({ variant: "destructive", title: "Error deleting case" });
    }
  }

  // Category CRUD handlers
  function openCategoryModal(category = null) {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name || "",
        color: category.color || "#EE2D24"
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({
        name: "",
        color: "#EE2D24"
      });
    }
    setCategoryModalOpen(true);
  }

  async function saveCategory() {
    try {
      if (editingCategory) {
        await updateCalendarCategory(editingCategory.id, categoryForm);
        toast({ title: "Category updated" });
      } else {
        await createCalendarCategory(categoryForm);
        toast({ title: "Category created" });
      }
      setCategoryModalOpen(false);
      loadData();
    } catch (e) {
      console.error(e);
      toast({ variant: "destructive", title: "Error saving category" });
    }
  }

  async function handleDeleteCategory(categoryId) {
    if (!window.confirm("Delete this category?")) return;
    try {
      await deleteCalendarCategory(categoryId);
      toast({ title: "Category deleted" });
      loadData();
    } catch (e) {
      console.error(e);
      toast({ variant: "destructive", title: "Error deleting category" });
    }
  }

  function toggleCaseExpanded(caseId) {
    setExpandedCases(prev => ({ ...prev, [caseId]: !prev[caseId] }));
  }

  const days = useMemo(() => {
    const n = daysInMonth(monthCursor);
    const year = monthCursor.getFullYear(); const month = monthCursor.getMonth();
    const arr = []; for(let i=1;i<=n;i++){ arr.push(new Date(year, month, i)); }
    return arr;
  }, [monthCursor]);

  // Calendar grid with proper day alignment
  const calendarGrid = useMemo(() => {
    const firstDay = ymFirst(monthCursor);
    const firstDayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const numDays = daysInMonth(monthCursor);
    
    // Create array with leading empty cells for alignment
    const grid = [];
    
    // Add empty cells for days before the first of the month
    // Convert Sunday (0) to be at end: Monday=0, Tuesday=1, ..., Sunday=6
    const startOffset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    for (let i = 0; i < startOffset; i++) {
      grid.push(null);
    }
    
    // Add actual days
    const year = monthCursor.getFullYear();
    const month = monthCursor.getMonth();
    for (let i = 1; i <= numDays; i++) {
      grid.push(new Date(year, month, i));
    }
    
    return grid;
  }, [monthCursor]);

  const eventsByDay = useMemo(() => {
    const map = {}; for (const ev of events){
      const key = ev.startDate || ev.date; // support either
      map[key] = map[key] || []; map[key].push(ev);
    }
    return map;
  }, [events]);

  function caseName(caseId){ const c = cases.find(x => x.id === caseId); if (c) return `${c.matterName || c.name || ""}${c.caseNumber? " ("+c.caseNumber+")": ""}`; return ""; }

  // List View computed
  const listRows = useMemo(() => {
    const start = new Date(fromDate);
    const end = new Date(start); end.setDate(end.getDate() + Math.min(365, Math.max(1, parseInt(rangeDays,10))));
    const fromISO = start.toISOString().slice(0,10);
    const toISO = end.toISOString().slice(0,10);
    
    // Filter by date range
    let filtered = events.filter(ev => (ev.startDate||ev.date) >= fromISO && (ev.startDate||ev.date) <= toISO);
    
    // Filter by case if selected
    if (selectedCaseFilter !== "all") {
      filtered = filtered.filter(ev => ev.caseId === selectedCaseFilter);
    }
    
    const byDay = {};
    for (const ev of filtered){ const d = (ev.startDate||ev.date); (byDay[d] = byDay[d] || []).push(ev); }
    const daysSorted = Object.keys(byDay).sort();
    return daysSorted.map(d => ({ date: d, items: byDay[d] }));
  }, [events, fromDate, rangeDays, selectedCaseFilter]);

  async function exportListToPDF() {
    try {
      // Dynamically import jsPDF
      const { jsPDF } = await import('jspdf');
      
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = margin;
      
      // Title
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text('SGT Calendar - List View', margin, yPos);
      yPos += 10;
      
      // Date range
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      const start = new Date(fromDate);
      const end = new Date(start);
      end.setDate(end.getDate() + parseInt(rangeDays, 10));
      doc.text(`Date Range: ${fromDate} to ${end.toISOString().slice(0, 10)}`, margin, yPos);
      yPos += 10;
      
      // Add events grouped by date
      for (const day of listRows) {
        // Check if we need a new page
        if (yPos > pageHeight - 40) {
          doc.addPage();
          yPos = margin;
        }
        
        // Date header
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(220, 38, 38); // Red color
        doc.text(day.date, margin, yPos);
        yPos += 7;
        
        // Events for this day
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(0, 0, 0);
        
        for (const ev of day.items) {
          const c = cases.find(x => x.id === ev.caseId);
          const caseTitle = c ? `${c.matterName || c.name || ''}${c.caseNumber ? ` (${c.caseNumber})` : ''}` : 'No case';
          const court = c ? `${c.courtSystem || ''}${c.courtName ? ` • ${c.courtName}` : ''}` : '';
          
          // Check if we need a new page
          if (yPos > pageHeight - 30) {
            doc.addPage();
            yPos = margin;
          }
          
          // Event type and title
          doc.setFont(undefined, 'bold');
          const eventHeader = `${ev.type || 'Event'}: ${ev.title || ''}`;
          const lines = doc.splitTextToSize(eventHeader, pageWidth - 2 * margin);
          doc.text(lines, margin + 5, yPos);
          yPos += lines.length * 5;
          
          // Case info
          doc.setFont(undefined, 'normal');
          if (caseTitle && caseTitle !== 'No case') {
            doc.text(`Case: ${caseTitle}`, margin + 5, yPos);
            yPos += 5;
          }
          
          if (court) {
            doc.text(`Court: ${court}`, margin + 5, yPos);
            yPos += 5;
          }
          
          // Time
          if (ev.startTime) {
            doc.text(`Time: ${ev.startTime}${ev.endTime ? ` - ${ev.endTime}` : ''}`, margin + 5, yPos);
            yPos += 5;
          }
          
          // Notes
          if (ev.notes) {
            doc.setFontSize(9);
            const notesLines = doc.splitTextToSize(`Notes: ${ev.notes}`, pageWidth - 2 * margin - 10);
            doc.text(notesLines, margin + 5, yPos);
            yPos += notesLines.length * 4;
            doc.setFontSize(10);
          }
          
          yPos += 3; // Space between events
        }
        
        yPos += 5; // Space between days
      }
      
      // Save the PDF
      const filename = `sgt-calendar-${fromDate}-to-${end.toISOString().slice(0, 10)}.pdf`;
      doc.save(filename);
      
      toast({ title: "PDF exported successfully" });
    } catch (e) {
      console.error(e);
      toast({ variant: "destructive", title: "Error exporting PDF" });
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold tracking-tight">SGT Calendar</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={()=>openCaseModal()}>+ Case</Button>
          <Button onClick={()=>openEventModal()}>+ Event</Button>
          <Button variant="secondary" onClick={()=>setMonthCursor(ymFirst(new Date()))}>Today</Button>
        </div>
      </div>
      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="month">Month</TabsTrigger>
          <TabsTrigger value="list">List View</TabsTrigger>
          <TabsTrigger value="cases">Cases</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
        </TabsList>

        <TabsContent value="month" className="mt-4">
          <div className="flex items-center gap-2 mb-2">
            <Button variant="secondary" onClick={()=>setMonthCursor(addMonths(monthCursor, -1))}>Prev</Button>
            <div className="text-sm text-muted-foreground">{monthCursor.toLocaleString(undefined, { month: 'long', year: 'numeric' })}</div>
            <Button variant="secondary" onClick={()=>setMonthCursor(addMonths(monthCursor, +1))}>Next</Button>
          </div>
          
          {/* Day of week headers */}
          <div className="grid grid-cols-7 gap-3 mb-2">
            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
              <div key={day} className="text-center text-sm font-semibold text-muted-foreground py-2">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-3">
            {calendarGrid.map((d, idx) => {
              if (!d) {
                // Empty cell for alignment
                return <div key={`empty-${idx}`} className="border rounded p-3 min-h-[160px] bg-muted/20"></div>;
              }
              
              const key = d.toISOString().slice(0,10);
              const list = eventsByDay[key] || [];
              const isToday = key === todayISO();
              
              return (
                <div key={key} className={`border rounded p-3 min-h-[160px] flex flex-col ${isToday ? 'ring-2 ring-primary' : ''}`}>
                  <div 
                    className={`text-sm font-semibold mb-2 cursor-pointer hover:text-primary transition-colors ${isToday ? 'text-primary' : 'text-foreground'}`}
                    onClick={() => openDayDetail(d, list)}
                  >
                    {d.getDate()}
                  </div>
                  <div className="space-y-1 flex-1">
                    {list.slice(0,3).map((ev, idx) => (
                      <div 
                        key={ev.id || idx} 
                        className="text-[11px] px-2 py-1 rounded cursor-pointer hover:opacity-80 transition-opacity truncate"
                        title={(ev.type||'Event')+': '+(ev.title||'')}
                        style={{ 
                          backgroundColor: getEventColor(ev.type),
                          color: '#ffffff'
                        }}
                        onClick={() => openEventModal(ev)}
                      >
                        {ev.type||'Event'}
                      </div>
                    ))}
                    {list.length > 3 && (
                      <div 
                        className="text-[10px] text-muted-foreground cursor-pointer hover:underline"
                        onClick={() => openDayDetail(d, list)}
                      >
                        +{list.length-3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="list" className="mt-4">
          <div className="flex items-end gap-3 mb-3">
            <div>
              <div className="text-xs text-muted-foreground mb-1">From</div>
              <input type="date" value={fromDate} onChange={(e)=>setFromDate(e.target.value)} className="border rounded px-2 py-1 text-sm"/>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Days</div>
              <Select value={String(rangeDays)} onValueChange={(v)=>setRangeDays(parseInt(v,10))}>
                <SelectTrigger className="w-[160px]"><SelectValue placeholder="Range"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">Next 30 days</SelectItem>
                  <SelectItem value="60">Next 60 days</SelectItem>
                  <SelectItem value="90">Next 90 days</SelectItem>
                  <SelectItem value="180">Next 6 months</SelectItem>
                  <SelectItem value="365">Next 12 months</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Case</div>
              <Select value={selectedCaseFilter} onValueChange={setSelectedCaseFilter}>
                <SelectTrigger className="w-[200px]"><SelectValue placeholder="Filter by case"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cases</SelectItem>
                  {cases.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}{c.caseNumber ? ` (${c.caseNumber})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button className="ml-auto" onClick={exportListToPDF}>Export PDF</Button>
          </div>
          <div className="space-y-3">
            {listRows.map(day => (
              <div key={day.date} className="rounded border">
                <div className="px-3 py-2 bg-secondary/40 text-sm font-medium">{day.date}</div>
                <div className="p-3 space-y-2">
                  {day.items.map((ev, idx) => {
                    const c = cases.find(x => x.id === ev.caseId);
                    const caseTitle = c ? `${c.matterName||c.name||""}${c.caseNumber? ` (${c.caseNumber})`:''}` : '';
                    const court = c ? `${c.courtSystem||''}${c.courtName? ` • ${c.courtName}`: ''}` : '';
                    return (
                      <div key={ev.id || idx} className="border rounded p-2">
                        <div className="flex items-center justify-between">
                          <div className="font-medium">{caseTitle}</div>
                          <div className="flex items-center gap-2">
                            <div className="text-xs" style={{ color: getEventColor(ev.type) }}>{ev.type||'Event'}</div>
                            <Button size="sm" variant="ghost" onClick={() => openEventModal(ev)}>Edit</Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteEvent(ev.id)}>Delete</Button>
                          </div>
                        </div>
                        <div className="text-sm">{ev.title||''}</div>
                        <div className="text-xs text-muted-foreground">{court}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="cases" className="mt-4">
          <div className="space-y-2">
            {cases.map((c) => {
              const isExpanded = expandedCases[c.id];
              return (
                <div key={c.id} className="border rounded">
                  <div className="p-3 flex items-center justify-between cursor-pointer hover:bg-secondary/20" onClick={() => toggleCaseExpanded(c.id)}>
                    <div className="flex items-center gap-2">
                      {isExpanded ? <ChevronDown className="size-4"/> : <ChevronRight className="size-4"/>}
                      <div className="font-medium">{c.name}{c.caseNumber ? ` (${c.caseNumber})` : ""}</div>
                    </div>
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" variant="outline" onClick={() => openCaseModal(c)}>Edit</Button>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteCase(c.id)}>Delete</Button>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="px-3 pb-3 space-y-2 border-t bg-secondary/5">
                      <div className="grid grid-cols-2 gap-4 pt-3">
                        <div>
                          <div className="text-xs text-muted-foreground">Case Number</div>
                          <div className="text-sm">{c.caseNumber || "—"}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Linked Matter</div>
                          <div className="text-sm">{c.matterName || "—"}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Court System</div>
                          <div className="text-sm">{c.courtSystem || "—"}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Court Name</div>
                          <div className="text-sm">{c.courtName || "—"}</div>
                        </div>
                      </div>
                      {c.description && (
                        <div className="pt-2">
                          <div className="text-xs text-muted-foreground">Description/Comments</div>
                          <div className="text-sm whitespace-pre-wrap">{c.description}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {cases.length === 0 && <div className="text-center text-muted-foreground py-8">No cases yet. Click "+ Case" to add one.</div>}
          </div>
        </TabsContent>

        <TabsContent value="categories" className="mt-4">
          <div className="flex justify-end mb-4">
            <Button onClick={() => openCategoryModal()}>+ Add Category</Button>
          </div>
          <div className="space-y-2">
            {categories.map((cat) => (
              <div key={cat.id} className="border rounded p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded" style={{ backgroundColor: cat.color }}></div>
                  <div className="font-medium">{cat.name}</div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openCategoryModal(cat)}>Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => handleDeleteCategory(cat.id)}>Delete</Button>
                </div>
              </div>
            ))}
            {categories.length === 0 && <div className="text-center text-muted-foreground py-8">No categories yet. Click "+ Add Category" to create one.</div>}
          </div>
        </TabsContent>
      </Tabs>

      {/* Event Modal */}
      <Dialog open={eventModalOpen} onOpenChange={setEventModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingEvent ? "Edit Event" : "New Event"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Case</Label>
              <Select value={eventForm.caseId || "none"} onValueChange={(v)=>setEventForm({...eventForm, caseId: v === "none" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="Select case (optional)"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {cases.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}{c.caseNumber ? ` (${c.caseNumber})` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={eventForm.type} onValueChange={(v)=>setEventForm({...eventForm, type: v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.name}>
                      <span className="flex items-center gap-2">
                        <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: cat.color }}></span>
                        {cat.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title</Label>
              <Input value={eventForm.title} onChange={(e)=>setEventForm({...eventForm, title: e.target.value})} placeholder="Event title"/>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Date</Label>
                <Input type="date" value={eventForm.startDate} onChange={(e)=>setEventForm({...eventForm, startDate: e.target.value})}/>
              </div>
              <div>
                <Label>Start Time</Label>
                <Input type="time" value={eventForm.startTime} onChange={(e)=>setEventForm({...eventForm, startTime: e.target.value})}/>
              </div>
              <div>
                <Label>End Time</Label>
                <Input type="time" value={eventForm.endTime} onChange={(e)=>setEventForm({...eventForm, endTime: e.target.value})}/>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={eventForm.notes} onChange={(e)=>setEventForm({...eventForm, notes: e.target.value})} placeholder="Additional notes" rows={3}/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEventModalOpen(false)}>Cancel</Button>
            <Button onClick={saveEvent}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Case Modal */}
      <Dialog open={caseModalOpen} onOpenChange={setCaseModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCase ? "Edit Case" : "New Case"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Case Name</Label>
              <Input value={caseForm.name} onChange={(e)=>setCaseForm({...caseForm, name: e.target.value})} placeholder="e.g., Smith v. Jones"/>
            </div>
            <div>
              <Label>Case Number</Label>
              <Input value={caseForm.caseNumber} onChange={(e)=>setCaseForm({...caseForm, caseNumber: e.target.value})} placeholder="e.g., 2025-CV-12345"/>
            </div>
            <div>
              <Label>Link to Matter (optional)</Label>
              <Select value={caseForm.matterId || "none"} onValueChange={(v)=>{
                if (v === "none") {
                  setCaseForm({...caseForm, matterId: "", matterName: ""});
                } else {
                  const mat = matters.find(m => m.id === v);
                  setCaseForm({...caseForm, matterId: v, matterName: mat ? mat.name : ""});
                }
              }}>
                <SelectTrigger><SelectValue placeholder="Select matter"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {matters.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Court System</Label>
              <Input value={caseForm.courtSystem} onChange={(e)=>setCaseForm({...caseForm, courtSystem: e.target.value})} placeholder="e.g., Federal, State"/>
            </div>
            <div>
              <Label>Court Name</Label>
              <Input value={caseForm.courtName} onChange={(e)=>setCaseForm({...caseForm, courtName: e.target.value})} placeholder="e.g., U.S. District Court"/>
            </div>
            <div>
              <Label>Additional Description and/or Comments</Label>
              <Textarea value={caseForm.description} onChange={(e)=>setCaseForm({...caseForm, description: e.target.value})} placeholder="Enter any additional notes or comments about this case" rows={3}/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setCaseModalOpen(false)}>Cancel</Button>
            <Button onClick={saveCase}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Modal */}
      <Dialog open={categoryModalOpen} onOpenChange={setCategoryModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCategory ? "Edit Category" : "New Category"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Category Name</Label>
              <Input value={categoryForm.name} onChange={(e)=>setCategoryForm({...categoryForm, name: e.target.value})} placeholder="e.g., Filing Deadline"/>
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex gap-2 items-center">
                <Input type="color" value={categoryForm.color} onChange={(e)=>setCategoryForm({...categoryForm, color: e.target.value})} className="w-20 h-10"/>
                <Input value={categoryForm.color} onChange={(e)=>setCategoryForm({...categoryForm, color: e.target.value})} placeholder="#EE2D24" className="flex-1"/>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setCategoryModalOpen(false)}>Cancel</Button>
            <Button onClick={saveCategory}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Day Detail Modal */}
      <Dialog open={dayDetailOpen} onOpenChange={setDayDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedDayDate && selectedDayDate.toLocaleDateString(undefined, { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {selectedDayEvents.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">No events on this date</div>
            ) : (
              selectedDayEvents.map((ev, idx) => {
                const c = cases.find(x => x.id === ev.caseId);
                const caseTitle = c ? `${c.matterName || c.name || ''}${c.caseNumber ? ` (${c.caseNumber})` : ''}` : '';
                const court = c ? `${c.courtSystem || ''}${c.courtName ? ` • ${c.courtName}` : ''}` : '';
                
                return (
                  <div 
                    key={ev.id || idx} 
                    className="border rounded-lg p-4 hover:bg-secondary/20 transition-colors cursor-pointer"
                    onClick={() => {
                      setDayDetailOpen(false);
                      openEventModal(ev);
                    }}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div 
                          className="px-3 py-1 rounded text-sm font-medium text-white"
                          style={{ backgroundColor: getEventColor(ev.type) }}
                        >
                          {ev.type || 'Event'}
                        </div>
                        {ev.startTime && (
                          <div className="text-sm text-muted-foreground">
                            {ev.startTime}{ev.endTime ? ` - ${ev.endTime}` : ''}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="font-medium mb-1">{ev.title || 'Untitled'}</div>
                    {caseTitle && (
                      <div className="text-sm text-muted-foreground mb-1">
                        <span className="font-medium">Case:</span> {caseTitle}
                      </div>
                    )}
                    {court && (
                      <div className="text-sm text-muted-foreground mb-1">
                        <span className="font-medium">Court:</span> {court}
                      </div>
                    )}
                    {ev.notes && (
                      <div className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">
                        {ev.notes}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setDayDetailOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
